import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Chip,
} from "@mui/material";
import { useParams } from "react-router-dom";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  CustomTableContainer,
  CustomTableHead,
} from "../../../components/TableComponent";
import {
  getCandidatesForJobAPI,
  getScreeningStatusAPI,
  getBestScreeningResult,
} from "../../../services/screeningService";
import ScreeningDetailDialog from "./ScreeningDetailDialog";
import { exportToCsv } from "../../../utils/exportCsv";

const POLL_INTERVAL_MS = 30000;

const LEAD_STATUS_CHIP = {
  in_progress: { label: "Pending", color: "info" },
  qualified: { label: "Qualified", color: "success" },
  "intent-qualified": { label: "Qualified (Intent)", color: "success" },
  disqualified: { label: "Disqualified", color: "error" },
  "completed-RNR": { label: "No Answer", color: "warning" },
  dnd: { label: "Do Not Call", color: "default" },
  paused: { label: "Paused", color: "default" },
};

const isPending = (screening) => {
  if (!screening || !screening.status) return true;
  return !screening.lead_status || screening.lead_status === "in_progress";
};

const ScreeningDashboard = () => {
  const { jobId } = useParams();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [screeningByResumeId, setScreeningByResumeId] = useState({});
  const [detailResumeId, setDetailResumeId] = useState(null);

  const refreshStatus = useCallback(
    async (resumeIds) => {
      const entries = await Promise.all(
        resumeIds.map(async (resumeId) => {
          try {
            const status = await getScreeningStatusAPI(jobId, resumeId);
            return [resumeId, status];
          } catch (_) {
            return [resumeId, null];
          }
        })
      );
      setScreeningByResumeId((prev) => {
        const next = { ...prev };
        entries.forEach(([resumeId, status]) => {
          if (status) next[resumeId] = status;
        });
        return next;
      });
    },
    [jobId]
  );

  useEffect(() => {
    if (!jobId) return;
    const load = async () => {
      setLoading(true);
      try {
        const data = await getCandidatesForJobAPI(jobId);
        setCandidates(data);
        await refreshStatus(data.map((c) => c["Resume Id"]));
      } catch (error) {
        console.error("Error loading screening dashboard:", error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [jobId, refreshStatus]);

  // Only candidates that have actually been scheduled show up on this dashboard —
  // scheduling itself happens from the Resume/Interview dashboards.
  const scheduledCandidates = candidates.filter((c) => screeningByResumeId[c["Resume Id"]]?.status);

  // Poll while any scheduled candidate's screening result is still pending
  useEffect(() => {
    const resumeIds = scheduledCandidates.map((c) => c["Resume Id"]);
    const anyPending = resumeIds.some((id) => isPending(screeningByResumeId[id]));
    if (!anyPending || resumeIds.length === 0) return;
    const interval = setInterval(() => refreshStatus(resumeIds), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [scheduledCandidates, screeningByResumeId, refreshStatus]);

  const renderStatusCell = (resumeId) => {
    const screening = screeningByResumeId[resumeId];
    if (!screening || !screening.status) {
      return <Chip label="Not scheduled" size="small" />;
    }
    const chip = LEAD_STATUS_CHIP[screening.lead_status] || LEAD_STATUS_CHIP.in_progress;
    return <Chip label={chip.label} size="small" color={chip.color} />;
  };

  // Multiple attempts can exist per candidate — always show the best scoring one.
  const getBestResult = (resumeId) => {
    const screening = screeningByResumeId[resumeId];
    return getBestScreeningResult(screening?.results || []);
  };

  const renderScoreCell = (resumeId) => {
    const best = getBestResult(resumeId);
    if (!best) return "—";
    return <Typography variant="body2">{best.interview_rating}</Typography>;
  };

  const renderRecordingCell = (resumeId) => {
    const best = getBestResult(resumeId);
    if (!best || !best.call_recording_link) return "—";
    return (
      <a
        href={best.call_recording_link}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#2563eb", textDecoration: "underline" }}
      >
        Recording
      </a>
    );
  };

  const handleExport = () => {
    const rows = scheduledCandidates.map((candidate) => {
      const resumeId = candidate["Resume Id"];
      const screening = screeningByResumeId[resumeId];
      const chip = LEAD_STATUS_CHIP[screening?.lead_status] || LEAD_STATUS_CHIP.in_progress;
      const best = getBestResult(resumeId);
      return [
        candidate["Candidate name"] || "",
        chip.label,
        best?.interview_rating ?? "",
        best?.call_recording_link || "",
      ];
    });
    exportToCsv(
      `screening-dashboard-${jobId}.csv`,
      ["Candidate Name", "Status", "Screening Score", "Recording"],
      rows
    );
  };

  if (!jobId) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
        <Typography variant="h5">Select a job from Job Management Section</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5" fontWeight={700}>
          Screening Dashboard
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Box display="flex" alignItems="center" px={2} py={1} borderRadius={3} bgcolor="#F4F6F8">
            <PeopleAltIcon sx={{ color: "#1976d2", mr: 1 }} />
            <Typography fontWeight={600}>{scheduledCandidates.length} Scheduled Candidates</Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            disabled={scheduledCandidates.length === 0}
          >
            Export
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px">
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading candidates...
          </Typography>
        </Box>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid rgba(0, 0, 0, 0.08)", overflow: "hidden" }}>
          <CustomTableContainer component={Paper} elevation={0} sx={{ boxShadow: "none", borderRadius: 2 }}>
            <Table>
              <CustomTableHead style={{ position: "sticky", top: 0, backgroundColor: "#202224", zIndex: 1 }}>
                <TableRow>
                  <TableCell style={{ color: "#fff", minWidth: "180px" }}>Candidate Name</TableCell>
                  <TableCell style={{ color: "#fff", minWidth: "140px" }}>Status</TableCell>
                  <TableCell style={{ color: "#fff", minWidth: "120px" }}>Screening Score</TableCell>
                  <TableCell style={{ color: "#fff", minWidth: "120px" }}>Recording</TableCell>
                  <TableCell style={{ color: "#fff", minWidth: "220px" }}>Action</TableCell>
                </TableRow>
              </CustomTableHead>
              <TableBody>
                {scheduledCandidates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No screening calls scheduled yet
                    </TableCell>
                  </TableRow>
                )}
                {scheduledCandidates.map((candidate, index) => {
                  const resumeId = candidate["Resume Id"];
                  const hasResults = (screeningByResumeId[resumeId]?.results?.length || 0) > 0;
                  return (
                    <TableRow key={index}>
                      <TableCell>{candidate["Candidate name"]}</TableCell>
                      <TableCell>{renderStatusCell(resumeId)}</TableCell>
                      <TableCell>{renderScoreCell(resumeId)}</TableCell>
                      <TableCell>{renderRecordingCell(resumeId)}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<InfoOutlinedIcon />}
                          onClick={() => setDetailResumeId(resumeId)}
                          disabled={!hasResults}
                        >
                          Detail
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CustomTableContainer>
        </Paper>
      )}

      <ScreeningDetailDialog
        open={Boolean(detailResumeId)}
        onClose={() => setDetailResumeId(null)}
        candidateName={
          candidates.find((c) => c["Resume Id"] === detailResumeId)?.["Candidate name"] || ""
        }
        results={screeningByResumeId[detailResumeId]?.results || []}
      />
    </Box>
  );
};

export default ScreeningDashboard;
